import { CampingStyleProps } from "~/interfaces"
import ePaymentType from "~/enums/ePaymentType"
import React, { forwardRef, useContext, useEffect, useMemo, useState } from "react"
import { AdminContext } from "~/contexts/AdminContext"
import { NewReservationContext } from "~/contexts/admin/NewReservationContext"
import ILineItem from "~/interfaces/ILineItem"
import DiscountProps from "~/interfaces/DiscountProps"
import IAddOn from "~/interfaces/IAddOn"
import PaymentTypeSelector from "~/components/Reservations/PaymentModal/PaymentTypeSelector"
import PaymentLinkForm from "~/components/Reservations/PaymentModal/PaymentLinkForm"
import StripeCardForm from "~/components/Reservations/PaymentModal/StripeCardForm"
import { formatMoneyFromCents } from "~/utils/formatMoney"
import { NewReservationAction } from "~/reducers/newReservation"
import ConfirmationModal from "~/components/Reservations/ConfirmationModal"
import { RESERVATION_KINDS } from "~/constants"

interface IPaymentMethodProps {
  selectedCampingStyle: CampingStyleProps
  paymentMethod: ePaymentType
  isCardInputValid: boolean
  setIsCardInputValid: React.Dispatch<React.SetStateAction<boolean>>
}

const PaymentMethod = forwardRef((props: IPaymentMethodProps, ref) => {
  const { camp, closeModal } = useContext(AdminContext)
  const {
    isAcceptingDeposit,
    inputEmail,
    inputPhone,
    inputFirstName,
    inputLastTime,
    order,
    lineItems,
    depositPreview,
    dispatch,
    newReservation,
    selectedCampingStyle,
    inputCamperId,
    setIsProcessing,
    setSelectedPaymentMethodType,
    selectedPaymentMethodType,
    isLoadingOrderPreview,
    setErrorResponse,
    selectedSendingMode
  } = useContext(NewReservationContext)

  const discountFromLineItems = (lineItems: Partial<ILineItem>[]): DiscountProps | undefined => {
    const discounts = [...(camp.discounts || []), ...(selectedCampingStyle.discounts || [])]

    const discountIds = lineItems?.map((li) => li.discount_id) || []
    return discounts.find((discount) => discountIds.includes(discount.id))
  }

  const [intentId, setIntentId] = useState<string>()
  const [products, setProducts] = useState<IAddOn[]>([])

  const [isTaxFeeEnabled, setIsTaxFeeEnabled] = useState(false)
  const [isServiceFeeEnabled, setIsServiceFeeEnabled] = useState(false)
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false)
  const [isPaid, setIsPaid] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState(null)
  const [errorMessage, setErrorMessage] = useState("")
  const [intervalId, setIntervalId] = useState<number>()
  const [showCloseButton, setShowCloseButton] = useState(false)

  useEffect(() => {
    if (newReservation.is_tax_enabled) setIsTaxFeeEnabled(newReservation.is_tax_enabled)
  }, [newReservation])

  const setPaymentMethod = (v: ePaymentType) => {
    dispatch({ type: NewReservationAction.UPDATE_PAYMENT_METHOD, payload: { paymentMethod: v } })
  }

  const handleChangePaymentType = (v: ePaymentType) => {
    setPaymentMethod(v)
    if (v === ePaymentType.CASH_PAID || v === ePaymentType.CASH_UNPAID) {
      setIsServiceFeeEnabled(false)
    } else {
      setIsServiceFeeEnabled(true)
    }
  }

  const handleConfirmationClose = () => {
    setShowCloseButton(true)
    setIsProcessing(false)
    closeModal()
  }

  const summaryOrder = isAcceptingDeposit ? depositPreview : order
  const paymentOptions = [ePaymentType.CARD_INPUT, ePaymentType.LINK, ePaymentType.CASH_PAID, ePaymentType.CASH_UNPAID]

  const paymentMethod = useMemo(() => {
    switch (props.paymentMethod) {
      case ePaymentType.LINK:
        return (
          <PaymentLinkForm
            email={inputEmail || ""}
            phone={inputPhone || ""}
            selectedSendingMode={selectedSendingMode}
            campingStyleId={props.selectedCampingStyle?.id}
            onSuccess={() => setIsPaymentSuccess(true)}
            isTaxFeeEnabled={isTaxFeeEnabled}
            isCreditCardFeeEnabled={newReservation?.is_credit_card_fee_enabled}
            discountSlug={discountFromLineItems(lineItems)?.slug}
            lineItems={lineItems}
            depositLineItems={depositPreview?.line_items}
            isNewReservation={true}
            width="w-full"
            order={order}
          />
        )
      case ePaymentType.CARD_READER:
      case ePaymentType.CARD_INPUT:
        return (
          <StripeCardForm
            ref={ref}
            camperId={order?.camper_id || inputCamperId}
            ctaText={`Charge ${formatMoneyFromCents(summaryOrder?.unpaid_total_in_cents, camp?.currency)}`}
            setPaymentIntentId={setIntentId}
            paymentIntentId={intentId}
            onSuccess={() => setIsPaymentSuccess(true)}
            order={order}
            campingStyleId={props.selectedCampingStyle?.id}
            isTaxFeeEnabled={isTaxFeeEnabled}
            isCreditCardFeeEnabled={newReservation?.is_credit_card_fee_enabled}
            discountSlug={discountFromLineItems(lineItems)?.slug}
            lineItems={lineItems}
            depositLineItems={depositPreview?.line_items}
            paymentMethod={props.paymentMethod}
            isNewReservation={true}
            paymentMethods={order?.payment_methods || []}
            setPaymentMethod={setPaymentMethod}
            errorMessage={errorMessage}
            setErrorMessage={v => {
              setErrorMessage(v)
              setErrorResponse(v)
            }}
            setPaymentStatus={setPaymentStatus}
            setIntervalId={setIntervalId}
            setShowCloseButton={setShowCloseButton}
            width="w-full"
            isCamperPresent={inputEmail || inputPhone || inputFirstName || inputLastTime}
            camper={{ email: inputEmail, phone: inputPhone, first_name: inputFirstName, last_name: inputLastTime }}
            isCardInputValid={props.isCardInputValid}
            setIsCardInputValid={props.setIsCardInputValid}
            isOrderRecurring={newReservation?.kind === RESERVATION_KINDS.RECURRING}
            hideCta
            isCreateV2
            setSelectedPaymentMethodType={setSelectedPaymentMethodType}
            selectedPaymentMethodType={selectedPaymentMethodType}
            isLoading={isLoadingOrderPreview}
          />
        )
      default:
        return <></>
    }
  }, [
    props.paymentMethod,
    inputEmail,
    inputPhone,
    selectedSendingMode,
    props.selectedCampingStyle?.id,
    isTaxFeeEnabled,
    newReservation?.is_credit_card_fee_enabled,
    lineItems,
    depositPreview?.line_items,
    order,
    ref,
    intentId,
    errorMessage,
    props.isCardInputValid,
    props.setIsCardInputValid,
    inputFirstName,
    inputLastTime,
    inputCamperId,
    setSelectedPaymentMethodType,
    isLoadingOrderPreview
  ])

  return (
    <div>
      <div className="mb-2 flex justify-between items-center">
        <label htmlFor="name" className="inline font-bold text-md text-gray-900">
          Payment method
        </label>
      </div>
      <PaymentTypeSelector
        options={paymentOptions}
        selectedOption={props.paymentMethod}
        onChange={handleChangePaymentType}
        isDeposit={isAcceptingDeposit}
      />
      {paymentMethod}
      {paymentStatus != null || showCloseButton ? (
        <ConfirmationModal
          paymentStatus={paymentStatus}
          setPaymentStatus={setPaymentStatus}
          paymentMethod={props.paymentMethod}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          onCtaClick={handleConfirmationClose}
          showCloseButton={showCloseButton}
          setShowCloseButton={setShowCloseButton}
          camp={camp}
          paymentIntentId={intentId}
          intervalId={intervalId}
        />
      ) : null}
    </div>
  )
})

export default PaymentMethod
